import { useEffect, useState } from 'react';
import { getModelLabel } from '../config/modelsMap';
import { Option } from 'shared/types/general';
import { IVersion } from '../types';
import { logService } from '../api/log.service';

type ActiveQuery = {
  model_name?: string;
  actor_name?: string;
  object_id?: string;
  fields?: Record<string, string>;
} | null;
type PaginationModel = { page: number; pageSize: number };

export const useVersionsData = () => {
  const [models, setModels] = useState<Option[]>([]);
  const [versions, setVersions] = useState<IVersion[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 20
  });
  const [loading, setLoading] = useState(false);
  const [activeQuery, setActiveQuery] = useState<ActiveQuery>(null);

  useEffect(() => {
    logService.getModels().then((res) => {
      setModels(
        res.map((item, index) => ({
          id: index,
          code: item.model,
          label: getModelLabel(item.model)
        }))
      );
    });
  }, []);

  const fetchPage = async (query: ActiveQuery, page: number, pageSize: number) => {
    if (!query) return;
    setLoading(true);
    try {
      const limit = pageSize;
      const offset = page * pageSize;
      const wantsGetVersions = Boolean(query.model_name && (query.object_id || query.fields));
      const res = wantsGetVersions
        ? await logService.getVersions(
            String(query.model_name),
            String(query.object_id ?? ''),
            query.fields ?? {},
            { limit, offset },
            query.actor_name?.trim() ? { actor_name: query.actor_name.trim() } : undefined
          )
        : await logService.getAllPaginated(limit, offset, query);

      const rawRows = res?.results ?? res?.logs ?? [];
      const rows = rawRows.map((item) => ({
        ...item,
        id: item.id
      }));
      setVersions(rows as IVersion[]);
      setRowCount(Number(res.count ?? res?.results?.count ?? rows.length));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeQuery) return;
    fetchPage(activeQuery, paginationModel.page, paginationModel.pageSize);
  }, [activeQuery, paginationModel.page, paginationModel.pageSize]);

  const handleFilter = (values: Record<string, any>) => {
    setVersions([]);
    const modelName = String(values?.model ?? '').trim();
    const actorName = String(values?.actor_name ?? '').trim();
    const rawObject = values?.object;
    const objectId = (() => {
      if (rawObject == null) return '';
      if (typeof rawObject === 'string' || typeof rawObject === 'number') {
        return String(rawObject).trim();
      }
      if (typeof rawObject === 'object') {
        const candidate =
          rawObject.id ??
          rawObject.code ??
          rawObject.object_id ??
          rawObject.value ??
          rawObject.ref ??
          '';
        return String(candidate).trim();
      }
      return '';
    })();

    if (!modelName && !actorName) {
      setActiveQuery(null);
      setRowCount(0);
      return;
    }
    const fields: Record<string, string> = {};
    Object.entries(values ?? {}).forEach(([key, raw]) => {
      if (key === 'model' || key === 'actor_name' || key === 'object') return;
      if (raw == null) return;
      if (typeof raw === 'string' || typeof raw === 'number') {
        const v = String(raw).trim();
        if (v) fields[key] = v;
        return;
      }
      if (typeof raw === 'object') {
        const candidate = (raw as any).id ?? (raw as any).code ?? (raw as any).ref ?? '';
        const v = String(candidate).trim();
        if (v) fields[key] = v;
      }
    });

    const query: {
      model_name?: string;
      actor_name?: string;
      object_id?: string;
      fields?: Record<string, string>;
    } = {};
    if (modelName) query.model_name = modelName;
    if (actorName) query.actor_name = actorName;
    if (objectId) query.object_id = objectId;
    if (modelName && Object.keys(fields).length > 0) query.fields = fields;
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setActiveQuery(query);
  };

  return {
    models,
    versions,
    rowCount,
    loading,
    paginationModel,
    setPaginationModel,
    handleFilter
  };
};
